import { createEffect, createMemo, createSignal, type Accessor } from 'solid-js'
import {
  resolveRefreshTier,
  type RefreshTicket,
} from '~/services/refresh-tickets'

// What the refresh button shows (GLOSSARY.md "Daily Picks refresh"): the
// current tier once the count has loaded. Nothing renders while the count is
// still loading (`pending`) or the tickets or count are unavailable
// (`hidden`); the two differ only in whether the count may still arrive.
export type DailyPicksRefreshState =
  | { status: 'hidden' }
  | { status: 'pending' }
  | { status: 'ready'; tier: RefreshTicket }

export interface DailyPicksRefresh {
  // One more refresh bought today (GLOSSARY.md "Refresh count"): each
  // committed refresh adds one locally, stepping the button to the next
  // tier without re-reading the stats.
  countRefresh: () => void
  // The daily-pick rollover's turn of the day (GLOSSARY.md "Refresh count").
  resetCount: () => void
  state: Accessor<DailyPicksRefreshState>
}

export function createDailyPicksRefresh(input: {
  countFailed: Accessor<boolean>
  seedCount: Accessor<number | undefined>
  tickets: Accessor<RefreshTicket[] | undefined>
}): DailyPicksRefresh {
  // Today's refresh count: seeded once from the stats read, then kept
  // locally. A rollover reset also counts as seeding — the server's day has
  // turned, so a stats read landing afterwards carries yesterday's count and
  // must not overwrite the new day's zero.
  const [count, setCount] = createSignal<number>()
  let seeded = false
  createEffect(() => {
    const seed = input.seedCount()
    if (seeded || seed === undefined) return
    seeded = true
    setCount(seed)
  })

  const state = createMemo<DailyPicksRefreshState>(() => {
    const tickets = input.tickets()
    if (!tickets || tickets.length === 0 || input.countFailed()) {
      return { status: 'hidden' }
    }
    const current = count()
    if (current === undefined) return { status: 'pending' }
    const tier = resolveRefreshTier(tickets, current)
    return tier ? { status: 'ready', tier } : { status: 'hidden' }
  })

  return {
    // A refresh can only commit once the count is known — the button has to
    // quote a tier to be tapped at all — so the unseeded fallback is only
    // there to satisfy the type.
    countRefresh: () => setCount((current) => (current ?? 0) + 1),
    resetCount: () => {
      seeded = true
      setCount(0)
    },
    state,
  }
}
